/* De projectpagina (/crm/projects/:projectId): alles van één project, zonder tabs
 * en zonder popup ervoor (ADR 0017). Gebruikt de volle breedte van de CRM-layout.
 *
 *   kop            navigatie (vorige/volgende, wisselaar), acties, naam, stadium, feiten
 *   sectiebalk     blijft boven staan; springt naar een sectie
 *   hoofdkolom     locatiemonitor (als er EMS is), offertes, bestanden
 *   rechterkolom   opvolgen (als er iets is, ADR 0028), gekoppeld en logboek; op smalle schermen eronder
 *
 * `?document=<file_id>` opent de documentweergave met feedback over de pagina heen (ADR 0021),
 * zodat een melding, een taak of een deeplink rechtstreeks naar een Versie kan. */

import { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { ProjectModal } from '@/components/crm/ProjectModal';
import {
  useBestanden, useFeedback, usePaginaProject, useGekoppeld, useLogboek, useMonitor, useOffertes,
} from './hooks';
import type { DocumentGroup } from '../../../supabase/functions/_shared/fileVersions';
import type { Bestand } from './data/bestanden';
import { ProjectKop } from './ui/ProjectKop';
import { ProjectNavigatie } from './ui/ProjectNavigatie';
import { Sectiebalk, type Sectie } from './ui/Sectiebalk';
import { MonitorBlok } from './ui/MonitorBlok';
import { OffertesBlok, gekoppeldeBestanden, offerteDocumenten } from './ui/OffertesBlok';
import { BestandenBlok, type BestandenBlokHandle } from './ui/BestandenBlok';
import { GekoppeldBlok, gekoppeldeKaarten } from './ui/GekoppeldBlok';
import { Logboek } from './ui/Logboek';
import { DocumentWeergave } from './ui/DocumentWeergave';
import { VraagFeedback } from './ui/VraagFeedback';
import { useProjectOpvolging, useVerstuurdeStukken } from '@/features/opvolging/hooks';
import { aantalOpvolging, heeftOpvolging } from '@/features/opvolging/data/opvolging';
import { OpvolgBlok } from '@/features/opvolging/ui/OpvolgBlok';

const ADMIN = new Set(['admin', 'super_admin']);

export function ProjectPagina({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { userRole, user } = useAuth();
  const isAdmin = ADMIN.has(userRole ?? '');
  const project = usePaginaProject(projectId);
  const monitor = useMonitor(projectId);
  const offertes = useOffertes(projectId);
  const gekoppeld = useGekoppeld(projectId);
  const logboek = useLogboek(projectId);
  const bestanden = useBestanden(projectId, project.data === undefined ? undefined : project.data?.sharepoint_folder_path ?? null);
  const feedback = useFeedback(projectId);
  const opvolging = useProjectOpvolging(projectId);
  const verstuurd = useVerstuurdeStukken(projectId);
  const bestandenRef = useRef<BestandenBlokHandle>(null);
  const kopRef = useRef<HTMLElement>(null);
  const [bewerken, setBewerken] = useState(false);
  const [params, setParams] = useSearchParams();
  const documentId = params.get('document');
  const [vraag, setVraag] = useState<{ doc: DocumentGroup<Bestand>; fileId?: string } | null>(null);

  const openDocument = useCallback((fileId: string) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set('document', fileId);
      return n;
    });
  }, [setParams]);
  const kiesVersie = useCallback((fileId: string) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set('document', fileId);
      return n;
    }, { replace: true });
  }, [setParams]);
  const sluitDocument = useCallback(() => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.delete('document');
      return n;
    });
  }, [setParams]);
  const vraagFeedback = useCallback((doc: DocumentGroup<Bestand>, fileId?: string) => setVraag({ doc, fileId }), []);

  if (project.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy>
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-9 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-10 w-2/3 animate-pulse rounded bg-muted" />
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="h-80 animate-pulse rounded-xl bg-muted" />
          <div className="h-80 animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (project.isError || !project.data) {
    return (
      <div className="py-16 text-center">
        <Target className="mx-auto mb-4 h-12 w-12 text-muted-foreground" aria-hidden />
        <h1 className="mb-2 text-lg font-medium">{project.isError ? 'Project kon niet geladen worden' : 'Project niet gevonden'}</h1>
        {project.isError && <p className="mb-4 text-sm text-muted-foreground">{(project.error as Error).message}</p>}
        <Button onClick={() => navigate('/crm/projects')}>Terug naar projecten</Button>
      </div>
    );
  }

  const p = project.data;
  const documenten = bestanden.data?.documenten ?? [];
  const aantalOffertes = (offertes.data?.length ?? 0) + offerteDocumenten(documenten, gekoppeldeBestanden(offertes.data ?? [])).length;
  const kaarten = gekoppeld.data ? gekoppeldeKaarten(gekoppeld.data) : [];
  const secties: Sectie[] = [
    ...(monitor.data ? [{ id: 'monitor', label: 'Locatiemonitor', live: true }] : []),
    { id: 'offertes', label: 'Offertes', aantal: offertes.data ? aantalOffertes : null },
    { id: 'bestanden', label: 'Bestanden', aantal: bestanden.data ? documenten.length : null },
    ...(heeftOpvolging(opvolging.data) ? [{ id: 'opvolging', label: 'Opvolgen', aantal: aantalOpvolging(opvolging.data) }] : []),
    ...(kaarten.length ? [{ id: 'gekoppeld', label: 'Gekoppeld', aantal: kaarten.length }] : []),
    { id: 'logboek', label: 'Logboek', aantal: logboek.data ? logboek.data.items.length + logboek.data.openTaken.length : null },
  ];

  return (
    <div className="flex flex-col gap-4">
      <ProjectKop
        kopRef={kopRef}
        project={p}
        monitor={monitor.data}
        gekoppeld={gekoppeld.data}
        navigatie={<ProjectNavigatie projectId={projectId} />}
        onBewerken={() => setBewerken(true)}
        onBestandToevoegen={() => bestandenRef.current?.kiesBestand()}
      />
      <Sectiebalk secties={secties} kop={kopRef} titel={[p.project_number, p.name].filter(Boolean).join(' · ')} />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]">
        <div className="flex min-w-0 flex-col gap-6">
          {monitor.data && <MonitorBlok monitor={monitor.data} />}
          <OffertesBlok project={p} offertes={offertes.data ?? []} documenten={documenten} />
          <BestandenBlok
            ref={bestandenRef}
            projectId={projectId}
            data={bestanden.data}
            isLoading={bestanden.isPending && bestanden.fetchStatus !== 'idle'}
            error={bestanden.error}
            onOpnieuw={() => bestanden.refetch()}
            magVersies={isAdmin}
            feedback={feedback.data}
            mijnId={user?.id ?? null}
            onOpenDocument={openDocument}
            onVraagFeedback={vraagFeedback}
            verstuurd={verstuurd.data}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          {opvolging.data && <OpvolgBlok opvolging={opvolging.data} mijnId={user?.id ?? null} />}
          {gekoppeld.data && <GekoppeldBlok gekoppeld={gekoppeld.data} />}
          {logboek.data && <Logboek projectId={projectId} logboek={logboek.data} magNotities={isAdmin} />}
        </div>
      </div>

      {documentId && (
        <DocumentWeergave
          projectId={projectId}
          fileId={documentId}
          bestanden={bestanden.data}
          bestandenLaden={bestanden.isPending && bestanden.fetchStatus !== 'idle'}
          feedback={feedback.data}
          mijnId={user?.id ?? null}
          onKiesVersie={kiesVersie}
          onSluit={sluitDocument}
          onVraagFeedback={vraagFeedback}
        />
      )}

      <VraagFeedback
        open={vraag !== null}
        onOpenChange={(o) => !o && setVraag(null)}
        projectId={projectId}
        projectNummer={p.project_number ?? null}
        doc={vraag?.doc ?? null}
        fileId={vraag?.fileId ?? null}
      />

      <ProjectModal
        isOpen={bewerken}
        onClose={() => setBewerken(false)}
        project={p.rij as any}
        onSaved={() => {
          setBewerken(false);
          project.refetch();
        }}
        templates={[]}
      />
    </div>
  );
}
